<script lang="ts">
  import { FarmaciaClinica, type DocumentoFiscal } from '../lib/api';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { resolveDisplayName, resolveLogoUrl, TOSCANA_LOGO } from '../lib/branding.svelte';

  let {
    documento,
    pacienteNombre = '',
    lineas = [],
    tenantNombre,
    logoUrl,
    onClose,
  }: {
    documento: DocumentoFiscal;
    pacienteNombre?: string;
    lineas?: Array<{ descripcion: string; cantidad: number; precioUnitario: number; subtotal?: number }>;
    tenantNombre?: string;
    logoUrl?: string | null;
    onClose?: () => void;
  } = $props();

  const branding = $derived(tenantCtx.branding);
  const nombre = $derived(
    tenantNombre
      ?? resolveDisplayName(branding, tenantCtx.nombre, tenantCtx.snapshot?.tenantId ?? 'Clínica'),
  );
  const logo = $derived(logoUrl ?? resolveLogoUrl(branding) ?? TOSCANA_LOGO);
  const brandColor = $derived(branding?.primaryColor ?? 'var(--brand-deep)');

  const esFactura = $derived(documento.tipo === 'FACTURA');
  const fechaStr = $derived(
    documento.fecha
      ? new Date(documento.fecha).toLocaleString('es-GT', { dateStyle: 'medium', timeStyle: 'short' })
      : new Date().toLocaleString('es-GT'),
  );

  let descargandoPdf = $state(false);
  let pdfError = $state<string | null>(null);

  function imprimir() {
    window.print();
  }

  async function descargarPdf() {
    if (!documento.id) return;
    descargandoPdf = true;
    pdfError = null;
    try {
      const name = `factura-${documento.serie}-${documento.numero}.pdf`;
      await FarmaciaClinica.descargarPdf(documento.id, name);
    } catch (e: any) {
      pdfError = e?.message ?? String(e);
    } finally {
      descargandoPdf = false;
    }
  }

  const puedePdfFel = $derived(
    esFactura && documento.id && documento.estado === 'CERTIFICADO',
  );
</script>

<div class="recibo-wrap">
  <div class="recibo-toolbar no-print">
    <button type="button" class="primary" onclick={imprimir}>Imprimir</button>
    {#if puedePdfFel}
      <button type="button" onclick={descargarPdf} disabled={descargandoPdf}>
        {descargandoPdf ? 'Descargando…' : 'PDF FEL'}
      </button>
    {/if}
    {#if onClose}<button type="button" onclick={onClose}>Cerrar</button>{/if}
  </div>
  {#if pdfError}<p class="pdf-err no-print">{pdfError}</p>{/if}

  <article class="recibo" style:--recibo-brand={brandColor}>
    <header>
      <img src={logo} alt="" class="recibo-logo" />
      <h1>{nombre}</h1>
      <p class="doc-type">{esFactura ? 'FACTURA ELECTRÓNICA (FEL)' : 'RECIBO INTERNO'}</p>
      <p class="doc-num">{documento.serie}-{documento.numero}</p>
      <p class="fecha">{fechaStr}</p>
    </header>

    {#if esFactura && documento.receptorNombre}
      <section class="receptor">
        <strong>Receptor:</strong> {documento.receptorNombre}
        {#if documento.receptorNit} · NIT {documento.receptorNit}{/if}
      </section>
    {:else if pacienteNombre}
      <section class="receptor"><strong>Paciente:</strong> {pacienteNombre}</section>
    {/if}

    {#if lineas.length > 0}
      <table>
        <thead><tr><th>Descripción</th><th>Cant.</th><th>P.U.</th><th>Subtotal</th></tr></thead>
        <tbody>
          {#each lineas as l (l.descripcion + l.cantidad)}
            <tr>
              <td>{l.descripcion}</td>
              <td class="num">{l.cantidad}</td>
              <td class="num">Q{Number(l.precioUnitario).toFixed(2)}</td>
              <td class="num">Q{(l.subtotal ?? l.cantidad * l.precioUnitario).toFixed(2)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}

    <footer class="totales">
      <div class="total-row"><span>Total</span><strong>Q{Number(documento.total).toFixed(2)}</strong></div>
      {#if esFactura && documento.iva != null}
        <div class="total-row iva"><span>IVA incluido</span><span>Q{Number(documento.iva).toFixed(2)}</span></div>
      {/if}
    </footer>

    {#if esFactura && documento.felAutorizacion}
      <section class="fel">
        <p>Autorización SAT: {documento.felAutorizacion}</p>
        {#if documento.felSerie && documento.felNumero}
          <p>FEL {documento.felSerie}-{documento.felNumero}</p>
        {/if}
        {#if documento.felProveedor}<p>Certificador: {documento.felProveedor}</p>{/if}
      </section>
    {:else if !esFactura}
      <p class="legal">Comprobante interno sin efecto tributario. No sustituye factura fiscal.</p>
    {/if}
  </article>
</div>

<style>
  .recibo-wrap { margin-top: 1rem; }
  .recibo-toolbar { display: flex; gap: .5rem; margin-bottom: .75rem; flex-wrap: wrap; }
  .recibo-toolbar button {
    padding: .45rem .9rem; border-radius: 8px; border: 1px solid var(--border, #ccc);
    background: var(--bg-surface, #fff); cursor: pointer; font: inherit;
  }
  .recibo-toolbar .primary {
    background: var(--recibo-brand, var(--brand-deep, #0f766e));
    color: #fff; border: 0;
  }
  .pdf-err { color: var(--danger, #900); font-size: .82rem; margin: .25rem 0 .5rem; font-family: inherit; }
  .recibo {
    max-width: 24rem; margin: 0 auto;
    border: 1px dashed var(--border, #bbb); padding: 1.25rem 1rem;
    font-family: "Segoe UI", system-ui, sans-serif; font-size: .85rem;
    background: #fff; color: #0b2e36;
  }
  header { text-align: center; margin-bottom: .75rem; border-bottom: 2px solid var(--recibo-brand, #005f73); padding-bottom: .65rem; }
  .recibo-logo { max-height: 48px; max-width: 160px; object-fit: contain; margin-bottom: .35rem; }
  header h1 { margin: 0; font-size: 1rem; font-weight: 700; color: var(--recibo-brand, #005f73); }
  .doc-type { margin: .25rem 0 0; font-size: .75rem; letter-spacing: .04em; color: #5b7480; }
  .doc-num { margin: .15rem 0; font-weight: 700; font-size: .95rem; }
  .fecha { margin: 0; font-size: .78rem; color: #555; }
  .receptor { margin-bottom: .65rem; font-size: .82rem; }
  table { width: 100%; border-collapse: collapse; margin: .5rem 0; font-size: .78rem; }
  th, td { padding: .25rem .2rem; border-bottom: 1px dotted #ccc; text-align: left; }
  th { color: var(--recibo-brand, #005f73); font-size: .72rem; }
  .num { text-align: right; }
  .totales { margin-top: .65rem; border-top: 2px solid var(--recibo-brand, #333); padding-top: .4rem; }
  .total-row { display: flex; justify-content: space-between; font-size: .9rem; }
  .total-row.iva { font-size: .75rem; color: #555; margin-top: .2rem; }
  .fel, .legal { margin-top: .75rem; font-size: .72rem; color: #444; line-height: 1.4; }
  .fel p { margin: .15rem 0; }

  @media print {
    .no-print { display: none !important; }
    .recibo { border: none; max-width: none; }
  }
</style>
